import { useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import moment from 'moment';
import {
  APP_EVENTS,
  captureAppEvent,
  ORDER_SELECTION_SOURCE,
  reportCameraConnectionIssue,
} from 'src/services/posthogEvents';

import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData } from 'src/models/GalenData/Order.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { I18NextNS } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';

import WarnNoConnectedCameraModal from '../custom_components/WarnNoConnectedCameraModal';

type MatchedFoundProps = {
  matchedPatient: GalenDataUser;
  openOrder: DeviceDataView<OrderData>;
  onGoBack: () => void;
  onResumedExam: (session: Partial<Session>) => void;
};

export default function MatchedFound({
  matchedPatient,
  openOrder,
  onGoBack,
  onResumedExam,
}: MatchedFoundProps) {
  const { t } = useTranslation(I18NextNS.COMMON);

  const { canStartExam, isCameraConnectionLost } = useCheckCameraConnection();

  const [
    shouldShowWarnNoConnectedCameraModal,
    setShouldShowWarnNoConnectedCameraModal,
  ] = useState(false);

  const orderDate = moment(openOrder.data.OrderStatusDatetime.value).format(
    'YYYY-MM-DD',
  );

  const handleContinue = () => {
    const session: Partial<Session> = {
      patientUserId: matchedPatient.userId,
      id: matchedPatient.patientId,
      firstName: matchedPatient.firstName,
      lastName: matchedPatient.lastName,
      dob: matchedPatient.dateOfBirth,
      orderId: openOrder.data.OrderId.value,
      siteId: openOrder.data.SiteId?.value,
      siteName: openOrder.data.SiteName?.value,
      template: openOrder.data.OrderType?.value,
    };
    onResumedExam(session);
    captureAppEvent(APP_EVENTS.ORDER_SELECTED, {
      selection_source: ORDER_SELECTION_SOURCE.DUPLICATE_MATCH,
    });
  };

  return (
    <div>
      <p
        className="text-danger fw-bold fs-5"
        data-test="patient-already-exists"
      >
        {t('patientFields.patientAlreadyExists')}
      </p>
      <Table>
        <thead>
          <tr>
            <th>{t('patientFields.DOB')}</th>
            <th>{t('patientFields.First Name')}</th>
            <th>{t('patientFields.Last Name')}</th>
            <th>{t('patientFields.PID')}</th>
            <th>{t('orderFields.dateCreated')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{matchedPatient.dateOfBirth}</td>
            <td>{matchedPatient.firstName}</td>
            <td>{matchedPatient.lastName}</td>
            <td>{matchedPatient.patientId}</td>
            <td>{orderDate}</td>
          </tr>
        </tbody>
      </Table>
      <div className="d-flex justify-content-between mt-3">
        <Button
          variant="primary-100"
          onClick={onGoBack}
          data-track="exam.matched-patient.previous"
          data-observe
        >
          {t('pages:newExam.previous')}
        </Button>
        <Button
          variant="primary"
          data-test="start-resume-order"
          onClick={() => {
            if (!canStartExam) {
              reportCameraConnectionIssue(isCameraConnectionLost);
              setShouldShowWarnNoConnectedCameraModal(true);
              return;
            }
            handleContinue();
          }}
          data-track="exam.matched-patient.continue"
          data-observe
        >
          {t('pages:newExam.startResumeExamOrder')}
        </Button>
      </div>
      <WarnNoConnectedCameraModal
        isVisible={shouldShowWarnNoConnectedCameraModal}
        onClose={() => setShouldShowWarnNoConnectedCameraModal(false)}
      />
    </div>
  );
}
